import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Layout } from '../components/Layout';
import { submissionApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { formatFileSize } from '../lib/utils';
import { 
  Upload as UploadIcon, 
  FileText, 
  X, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  FileUp
} from 'lucide-react';

const UploadPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [course, setCourse] = useState('');
  const [submissionType, setSubmissionType] = useState('final');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.oasis.opendocument.text',
    'application/rtf',
    'text/plain'
  ];

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError('');
    
    if (rejectedFiles.length > 0) {
      setError('Invalid file type. Please upload PDF, DOCX, DOC, ODT, RTF, or TXT files.');
      return;
    }

    const selectedFile = acceptedFiles[0];
    if (selectedFile) {
      if (selectedFile.size > 100 * 1024 * 1024) {
        setError('File size exceeds 100MB limit');
        return;
      }
      setFile(selectedFile);
      if (!title) {
        setTitle(selectedFile.name.replace(/\.[^/.]+$/, ''));
      }
    }
  }, [title]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.oasis.opendocument.text': ['.odt'],
      'application/rtf': ['.rtf'],
      'text/plain': ['.txt']
    },
    maxFiles: 1,
    multiple: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload');
      return;
    }
    
    if (!title.trim()) {
      setError('Please enter a document title');
      return;
    }

    setUploading(true);
    setError('');
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('course', course);
    formData.append('submission_type', submissionType);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await submissionApi.upload(formData);
      
      clearInterval(progressInterval);
      setProgress(100);
      setSuccess(true);
      
      setTimeout(() => {
        navigate(`/submission/${response.data.id}`);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setFile(null);
    setProgress(0);
  };

  if (success) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-serif text-2xl font-bold text-foreground mb-2">
                Document Submitted Successfully!
              </h2>
              <p className="text-muted-foreground mb-4">
                Your document is being analyzed. You'll be redirected to view the results.
              </p>
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="upload-page">
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-bold text-foreground">Upload Document</h1>
          <p className="text-muted-foreground mt-1">
            Submit your academic document for plagiarism and AI content analysis
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* File Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Select File</CardTitle>
              <CardDescription>
                Supported formats: PDF, DOCX, DOC, ODT, RTF, TXT (Max 100MB)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!file ? (
                <div
                  {...getRootProps()}
                  className={`dropzone ${isDragActive ? 'active' : ''}`}
                  data-testid="file-dropzone"
                >
                  <input {...getInputProps()} data-testid="file-input" />
                  <FileUp className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="font-medium text-foreground mb-1">
                    {isDragActive ? 'Drop your file here' : 'Drag and drop your file here'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    or click to browse from your computer
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{file.name}</p>
                      <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeFile}
                    disabled={uploading}
                    data-testid="remove-file-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {uploading && (
                <div className="mt-4">
                  <Progress value={progress} className="h-2" />
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    Uploading... {progress}%
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Document Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Document Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Document Title *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter document title"
                  disabled={uploading}
                  required
                  data-testid="document-title-input"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="course">Course / Module (Optional)</Label>
                <Input
                  id="course"
                  value={course}
                  onChange={(e) => setCourse(e.target.value)}
                  placeholder="e.g., CS101 - Introduction to Computing"
                  disabled={uploading}
                  data-testid="course-input"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Submission Type</Label>
                <Select
                  value={submissionType}
                  onValueChange={setSubmissionType}
                  disabled={uploading}
                >
                  <SelectTrigger className="h-12" data-testid="submission-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="final">Final Submission</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full h-12"
            disabled={!file || uploading}
            data-testid="submit-upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <UploadIcon className="mr-2 h-4 w-4" />
                Submit for Analysis
              </>
            )}
          </Button>

          {/* Info Box */}
          <div className="bg-muted/50 border border-border rounded-sm p-4">
            <h4 className="font-medium text-foreground mb-2">What happens next?</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Your document will be analyzed against billions of web sources</li>
              <li>• AI-generated content detection will be performed</li>
              <li>• Results are typically ready within 1-3 minutes</li>
              <li>• You can download or email your detailed report</li>
            </ul>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default UploadPage;
